import { faClipboardList, faDryer } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '~/helpers/yupResolver';
import classNames from 'classnames';
import { convertToRaw, EditorState, RawDraftContentState } from 'draft-js';
import { isEqual } from 'lodash';
import { nanoid } from 'nanoid';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SubmitFunction, useNavigate, useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import * as yup from 'yup';
import { Prompt } from '~/components/Prompt';
import { ControlledStatusToggle } from '~/components/form/StatusToggle';
import { Button } from '~/components/form/SubmitButton';
import { JobWhatSection } from '~/components/job-form-sections';
import { taskTemplateToTaskInput } from '~/components/job/CreateJobForm/ManageTasks';
import { CustomTaskForm } from '~/components/job/CustomTaskForm';
import { TaskInput } from '~/generated/graphql';
import { getFragmentData } from '~/gql';
import {
  JobTemplateFieldsFragment,
  JobTemplateInput,
  Status,
  TemplateBehavior,
} from '~/gql/graphql';
import { JobTemplateFields } from '~/graphql/fragment/JobTemplateFields';
import { parseJson } from '~/helpers';
import { splice } from '~/helpers/array';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { SideLayout } from '~/layouts/side/SideLayout';
import { useJobTemplateListContext } from '~/routes/job-templates._index';
import { TagEntityType } from '../TagCombobox';
import { useTags } from '../TagCombobox/tags';
import {
  getTemplateBehavior,
  toGraphQLBehavior,
} from '../job/AssetDrivenTemplateIndicator';
import { Accordion } from '../ui/Accordion';
import { Switch } from '../ui/nucleus/Switch';
import { toAttachmentInput, toFormValue, UploadResult } from '../upload2';

type TemplateType = 'static' | 'asset_rules';

type FormValues = {
  status: Status;
  // TODO: If you add/update this field, also update AutomationForm.templateData.behavior to keep them consistent
  behavior: TemplateType;
  // TODO: If you add/update this field, also update AutomationForm.templateData.name to keep them consistent
  name: string;
  // TODO: If you add/update this field, also update AutomationForm.templateData.tasks to keep them consistent
  tasks: TaskInput[];
  // TODO: If you add/update this field, also update AutomationForm.templateData.notes to keep them consistent
  notes: RawDraftContentState | null;
  // TODO: If you add/update this field, also update AutomationForm.templateData.tags to keep them consistent
  tags: string[] | null;
  // TODO: If you add/update this field, also update AutomationForm.templateData.includeAttributes to keep them consistent
  includeAttributes: string[] | null;
  // TODO: If you add/update this field, also update AutomationForm.templateData.jobAttributes to keep them consistent
  jobAttributes: string[] | null;
  // TODO: If you add/update this field, also update AutomationForm.templateData.attachments to keep them consistent
  attachments: UploadResult[];
  fromTemplate: string;
  guestyCleaningStatus: boolean;
  quickCreate: boolean;
};

export const createEmptyNotesContent = (): RawDraftContentState =>
  convertToRaw(EditorState.createEmpty().getCurrentContent());

export const deserializeNotes = (
  notes: string | null | undefined
): RawDraftContentState => {
  if (!notes) {
    return createEmptyNotesContent();
  }

  try {
    const parsed = JSON.parse(notes) as RawDraftContentState;
    if (parsed?.blocks) {
      return parsed;
    }
  } catch {
    // fall through to plain-text conversion
  }

  return {
    blocks: [
      {
        key: 'legacy',
        text: notes,
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {},
      },
    ],
    entityMap: {},
  };
};

type TemplateTypeSelectorProps = {
  value: TemplateType;
  onChange: (value: TemplateType) => void;
  disabled?: boolean;
};

const TemplateTypeSelector = ({
  value,
  onChange,
  disabled = false,
}: TemplateTypeSelectorProps) => {
  const { t } = useTranslation();

  const templateTypes = [
    {
      type: 'static' as TemplateType,
      icon: faClipboardList,
      title: t('jobTemplate.type.static.title', 'Standard'),
      subtitle: t('jobTemplate.type.static.subtitle', 'all task types'),
      description: t(
        'jobTemplate.type.static.description',
        'The jobs created will match the template exactly'
      ),
    },
    {
      type: 'asset_rules' as TemplateType,
      icon: faDryer,
      title: t('jobTemplate.type.assetRules.title', 'Asset-Driven'),
      subtitle: t('jobTemplate.type.assetRules.subtitle', 'asset tasks only'),
      description: t(
        'jobTemplate.type.assetRules.description',
        'Defines rules that create tasks automatically based on selected assets. '
      ),
    },
  ];

  return (
    <div className='grid grid-cols-1 gap-3 pb-3 md:grid-cols-2'>
      {templateTypes.map((templateType) => (
        <button
          key={templateType.type}
          type='button'
          onClick={() => !disabled && onChange(templateType.type)}
          disabled={disabled}
          className={classNames(
            'group relative flex flex-col rounded-xl border-2 p-4 transition-all duration-200',
            'min-h-[120px] text-left',
            value === templateType.type
              ? 'border-brand bg-shade-8 shadow-md'
              : 'border-grey-20 bg-white hover:border-grey-40 hover:shadow-sm',
            disabled && 'cursor-not-allowed opacity-50'
          )}
          aria-label={`Select ${templateType.title}`}
          aria-pressed={value === templateType.type}
          tabIndex={0}
        >
          {/* Icon */}
          <div
            className={classNames(
              'mb-3 flex h-10 w-10 items-center justify-center rounded-full transition-colors',
              value === templateType.type
                ? 'bg-shade-25 text-brand'
                : 'bg-grey-5 text-grey-50 group-hover:bg-grey-10'
            )}
          >
            <FontAwesomeIcon icon={templateType.icon} className='h-5 w-5' />
          </div>

          {/* Title */}
          <h3
            className={classNames(
              'mb-1 text-base font-semibold',
              value === templateType.type ? 'text-brand' : 'text-grey-90'
            )}
          >
            {templateType.title}
          </h3>

          {/* Subtitle */}
          <p className='mb-2 text-sm font-medium capitalize text-brand'>
            {templateType.subtitle}
          </p>

          {/* Description */}
          <p
            className={classNames(
              'text-sm',
              value === templateType.type ? 'text-grey-70' : 'text-grey-60'
            )}
          >
            {templateType.description}
          </p>
        </button>
      ))}
    </div>
  );
};

type TemplateTypeDisplayProps = {
  value: TemplateType;
};

const TemplateTypeDisplay = ({ value }: TemplateTypeDisplayProps) => {
  const { t } = useTranslation();

  const templateTypes = [
    {
      type: 'static' as TemplateType,
      icon: faClipboardList,
      title: t('jobTemplate.type.static.title', 'Standard'),
      subtitle: t('jobTemplate.type.static.subtitle', 'all task types'),
      description: t(
        'jobTemplate.type.static.description',
        'The jobs created will match the template exactly'
      ),
    },
    {
      type: 'asset_rules' as TemplateType,
      icon: faDryer,
      title: t('jobTemplate.type.assetRules.title', 'Asset-Driven'),
      subtitle: t('jobTemplate.type.assetRules.subtitle', 'asset tasks only'),
      description: t(
        'jobTemplate.type.assetRules.description',
        'Defines rules that create tasks automatically based on selected assets. '
      ),
    },
  ];

  const selectedType = templateTypes.find((type) => type.type === value);

  if (!selectedType) return null;

  return (
    <div className='mb-4'>
      <div className='relative flex items-center gap-4 rounded-xl border border-outline-variant bg-white p-4 text-left'>
        {/* Icon */}
        <div className='flex h-16 w-16 items-center justify-center rounded-full bg-shade-25 text-lg text-brand'>
          <FontAwesomeIcon icon={selectedType.icon} className='h-5 w-5' />
        </div>

        <div className='align-start flex flex-col'>
          {/* Title */}
          <h3 className='text-base font-semibold text-brand'>
            {selectedType.title}
          </h3>

          {/* Subtitle */}
          <p className='mb-1 text-sm font-medium capitalize text-secondary'>
            {selectedType.subtitle}
          </p>

          {/* Description */}
          <p className='text-sm text-grey-70'>{selectedType.description}</p>
        </div>
      </div>
    </div>
  );
};

const safelyDeserializeTemplateNotes = (
  notes: string | null | undefined,
  templateId?: string
): RawDraftContentState | null => {
  try {
    return deserializeNotes(notes);
  } catch (error) {
    console.error('Failed to deserialize template notes', {
      error,
      templateId,
    });
    return null;
  }
};

export const serializeNotes = (
  notes: RawDraftContentState | null | undefined
): string | null => {
  if (!notes) return null;
  return JSON.stringify(notes);
};

const createValidationSchema = (
  currentName: string | undefined,
  templates: readonly { name: string }[]
) =>
  yup.object().shape({
    name: yup
      .string()
      .required('Required')
      .test('unique', 'Name must be unique', (value) => {
        return (
          currentName === value ||
          !templates.some((template) => template.name === value)
        );
      }),
  });

type Props = {
  data?: {
    integrations: string[];
  };
  clone?: boolean;
  onClose?: () => void;
  onSubmit: SubmitFunction;
  isSubmittingExternal?: boolean;
};

export const JobTemplateForm = ({
  data,
  clone,
  onClose,
  onSubmit,
  isSubmittingExternal = false,
}: Props) => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { t } = useTranslation();
  const { templates } = useJobTemplateListContext();
  const [allTags] = useTags(TagEntityType.Job);
  const { isMobile } = useBreakpoint();
  const [editTask, setEditTask] = useState<string | null>(null);
  // TODO: If you add/update tasks handling, also update AutomationForm.templateData.tasks to keep them consistent
  const [tasks, setTasks] = useState<TaskInput[]>([]);
  const [isTasksDirty, setIsTasksDirty] = useState(false);
  const [openPanelIdx, setOpenPanelIdx] = useState<number>(0);
  const [quickCreatePanelIdx, setQuickCreatePanelIdx] = useState<number>(-1);

  const template = getFragmentData(
    JobTemplateFields,
    templates?.find(({ id }) => id === templateId)
  );

  // Prepare templates for JobWhatSection - must be before early return
  const templateOptions = useMemo(() => {
    return (
      templates?.map((template) =>
        getFragmentData(JobTemplateFields, template)
      ) ?? []
    );
  }, [templates]);

  useEffect(() => {
    if (template) {
      setTasks(template.tasks.map(taskTemplateToTaskInput));
      setIsTasksDirty(false);
    }
  }, [template]);

  useEffect(() => {
    const original = template?.tasks.map(taskTemplateToTaskInput);
    setIsTasksDirty(!isEqual(tasks, original));
  }, [tasks, template?.tasks]);

  const initialValues = {
    fromTemplate: '',
    status: template?.status ?? Status.Active,
    behavior: getTemplateBehavior(template),
    name: template?.name ?? '',
    tags: template?.tags?.map((tag) => tag.id) ?? [],
    notes: safelyDeserializeTemplateNotes(template?.notes, template?.id),
    includeAttributes: template?.includeAttributes ?? null,
    jobAttributes: template?.jobAttributeIds ?? null,
    attachments: (template?.attachments ?? []).map(toFormValue),
    guestyCleaningStatus: template?.guestyCleaningStatus === 'clean',
    quickCreate: template?.quickCreate ?? false,
  };

  const guestyEnabled = data?.integrations?.includes('guesty') ?? false;

  const {
    register,
    formState: { isDirty, isSubmitting: loading, errors },
    control,
    setValue,
    handleSubmit,
    watch,
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(
      createValidationSchema(
        clone ? undefined : template?.name,
        templates ?? []
      )
    ),
  });

  const name = watch('name');
  const behavior = watch('behavior');
  const isBusy = loading || isSubmittingExternal;

  // Computed summary for accordion button
  const whatInfo = useMemo(() => {
    return name || null;
  }, [name]);

  // Computed summary for setup section
  const setupInfo = useMemo(() => {
    if (behavior === 'asset_rules') {
      return t('jobTemplate.type.assetRules.title', 'Asset-Driven');
    }
    return t('jobTemplate.type.static.title', 'Standard');
  }, [behavior, t]);

  // Helper to check if a task is the default rule task
  // null/undefined means default rule (catch-all), not empty array (empty = user hasn't selected types yet)
  const isDefaultRuleTask = useCallback((task: TaskInput): boolean => {
    return (
      task.type === 'Asset' &&
      task.asset?.selection === 'all' &&
      task.asset?.assetTypes == null // Loose null check: treats null and undefined as equivalent (GraphQL can return null)
    );
  }, []);

  // Helper to create the default rule task
  const createDefaultRuleTask = useCallback((): TaskInput => {
    const editorState = EditorState.createEmpty();
    const defaultDescription = convertToRaw(editorState.getCurrentContent());

    return {
      id: nanoid(),
      type: 'Asset',
      name: 'Default Task',
      description: JSON.stringify(defaultDescription),
      asset: {
        selection: 'all',
        assetTypes: undefined, // undefined means match all asset types
        allow: ['edit'],
      },
    };
  }, []);

  // Create/remove default task when behavior changes
  // For asset-driven templates, automatically create a default "catch-all" task
  // that matches all asset types (selection: 'all', assetTypes: null/undefined)
  // Only run this after initial render when user actively changes the behavior
  useEffect(() => {
    // Skip if behavior hasn't been set yet (initial render)
    if (behavior === undefined) return;

    if (behavior === 'asset_rules') {
      // Asset-driven template: ensure default task exists
      const hasDefaultTask = tasks.some(
        (task) => !task._destroy && isDefaultRuleTask(task)
      );

      if (!hasDefaultTask) {
        // Add default task if it doesn't exist
        setTasks((prevTasks) => [...prevTasks, createDefaultRuleTask()]);
      }
    } else if (behavior === 'static') {
      // Not asset-driven: mark default tasks for deletion (preserves server-side deletes)
      const hasDefaultTask = tasks.some(
        (task) => !task._destroy && isDefaultRuleTask(task)
      );

      if (hasDefaultTask) {
        // Mark default tasks with _destroy = true instead of filtering them out
        // This ensures server-side deletes are preserved
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            isDefaultRuleTask(task) ? { ...task, _destroy: true } : task
          )
        );
      }
    }
  }, [behavior, isDefaultRuleTask, createDefaultRuleTask, tasks]);

  // Panel toggle handler
  const handlePanelToggle = useCallback(
    async (panelIndex: number | undefined) => {
      const nextPanelIndex = panelIndex === undefined ? -1 : panelIndex;
      setOpenPanelIdx(nextPanelIndex);
    },
    []
  );

  // Quick Create accordion toggle handler
  const handleQuickCreatePanelToggle = useCallback(
    (panelIndex: number | undefined) => {
      const nextPanelIndex = panelIndex === undefined ? -1 : panelIndex;
      setQuickCreatePanelIdx(nextPanelIndex);
    },
    []
  );

  // Derive the current task from tasks and editTask to avoid stale state
  const task = useMemo(() => {
    return tasks?.find((task) => task.id === editTask) || null;
  }, [tasks, editTask]);

  const shownTaskType = useMemo(() => {
    return task?.type || null;
  }, [task]);

  // Close inline task editor if the currently edited task is no longer present
  // in the underlying tasks array (e.g. when tasks are replaced or cleared).
  // This avoids a stale editor without re-syncing on every tasks change.
  useEffect(() => {
    if (!editTask) {
      return;
    }

    const currentTasks = tasks || [];
    const taskStillExists = currentTasks.some((task) => task.id === editTask);

    if (!taskStillExists) {
      setEditTask(null);
    }
  }, [editTask, tasks]);

  // Handle template selection for UseTemplate component - must be before early return
  const handleUseTemplateSelect = useCallback(
    (selectedTemplate: JobTemplateFieldsFragment) => {
      if (selectedTemplate.tags) {
        setValue(
          'tags',
          selectedTemplate.tags.map((tag) => tag.id)
        );
      }
      if (selectedTemplate.notes) {
        setValue(
          'notes',
          safelyDeserializeTemplateNotes(
            selectedTemplate.notes,
            selectedTemplate.id
          )
        );
      }
      if (selectedTemplate.includeAttributes) {
        setValue('includeAttributes', selectedTemplate.includeAttributes);
      }
      if (selectedTemplate.jobAttributeIds) {
        setValue('jobAttributes', selectedTemplate.jobAttributeIds);
      }
      if (selectedTemplate.attachments) {
        setValue('attachments', selectedTemplate.attachments.map(toFormValue));
      }
    },
    [setValue]
  );

  const submit = async () => {
    await handleSubmit(async (values) => {
      onSubmit(serialize(toInput(tasks, values)), { method: 'post' });
    })();
  };
  const updateTaskType = useCallback(
    (taskId: string, taskType: string) => {
      const tasksListCopy = tasks.slice();
      const taskIndex = tasksListCopy.findIndex((task) => task.id === taskId);
      if (taskIndex < 0) return;

      const task = tasksListCopy[taskIndex];

      // Prevent changing type of default rule task (only applies to asset-driven templates)
      if (behavior === 'asset_rules') {
        // Use the helper function for consistent default rule detection
        if (isDefaultRuleTask(task) && taskType !== 'Asset') {
          // Don't allow changing default task type
          return;
        }
      }

      tasksListCopy[taskIndex].type = taskType;
      setTasks(tasksListCopy);
    },
    [tasks, behavior, isDefaultRuleTask]
  );

  const updateTask = useCallback((data: TaskInput) => {
    setTasks((tasks) => {
      const taskIndex = tasks.findIndex(({ id }) => id === data.id);
      if (taskIndex < 0) return tasks;

      if (data._destroy) {
        return splice(tasks, taskIndex, 1);
      } else {
        // Merge updated data with existing task to preserve all properties
        const existingTask = tasks[taskIndex];
        // Only include properties that are explicitly provided (not undefined)
        // This preserves task-type-specific data (asset, item, etc.) when task type changes
        // For example, if data.asset is undefined, it won't overwrite existingTask.asset
        const dataWithoutUndefined = Object.fromEntries(
          Object.entries(data).filter(([, value]) => value !== undefined)
        );
        const updatedTask: TaskInput = {
          ...existingTask,
          ...dataWithoutUndefined,
        };

        return splice(tasks, taskIndex, 1, updatedTask);
      }
    });

    setEditTask(null);
  }, []);

  const handleDeleteTask = useCallback((id: string) => {
    setTasks((tasks) => {
      const task = tasks.find((t) => t.id === id);
      if (!task) return tasks;

      // Mark for deletion
      return splice(
        tasks,
        tasks.findIndex((t) => t.id === id),
        1,
        { ...task, _destroy: true }
      );
    });
    setEditTask(null);
  }, []);

  // Stable callback for task editor type updates to prevent re-renders
  const handleTaskEditorUpdateTaskType = useCallback(
    (id: string, type: string) => {
      if (task && id === task.id) {
        updateTaskType(id, type);
      }
    },
    [task, updateTaskType]
  );

  const handleTaskEditorClose = useCallback(() => {
    setEditTask(null);
  }, []);

  // Check if current task is the default rule task (only applies to asset-driven templates)
  const isCurrentTaskDefaultRule = useMemo(() => {
    if (!task || behavior !== 'asset_rules') return false;
    // Use the helper function for consistent default rule detection
    return isDefaultRuleTask(task);
  }, [task, behavior, isDefaultRuleTask]);

  // Memoize isTaskTypeEditable function to prevent unnecessary re-renders of task list
  // This function determines if a task's type can be edited in the UI
  const isTaskTypeEditable = useCallback(
    (task: TaskInput) => {
      // Disable task type editing for default rule tasks (only applies to asset-driven templates)
      if (behavior !== 'asset_rules') return true;
      // Use the helper function for consistent default rule detection
      return !isDefaultRuleTask(task);
    },
    [behavior, isDefaultRuleTask]
  );

  // CRITICAL: Memoize taskState array to prevent JobWhatSection from re-rendering unnecessarily
  // Creating [tasks, setTasks] inline creates a new array reference on every render,
  // causing JobWhatSection to see it as a prop change and re-render (causing the blink)
  // setTasks is stable from useState, so we only need tasks in the dependency array
  const taskState = useMemo(
    () =>
      [tasks, setTasks] as [TaskInput[], Dispatch<SetStateAction<TaskInput[]>>],
    [tasks]
  );

  const taskSection = task ? (
    <CustomTaskForm
      key={task.id}
      task={task}
      onApply={updateTask}
      onClose={handleTaskEditorClose}
      onDelete={handleDeleteTask}
      showTaskOptions
      isJobTemplate
      taskType={shownTaskType}
      updateTaskType={handleTaskEditorUpdateTaskType}
      scopeIds={undefined}
      locationId={undefined}
      canEditType={
        behavior === 'asset_rules' ? !isCurrentTaskDefaultRule : true
      }
      allowedTaskTypes={
        behavior === 'asset_rules' && isCurrentTaskDefaultRule
          ? ['Asset']
          : undefined
      }
      disableSpecificAssetSelection={behavior === 'asset_rules'}
      isAssetDrivenTemplate={behavior === 'asset_rules'}
      isRuleContext={behavior === 'asset_rules'}
      title={
        behavior === 'asset_rules'
          ? t('jobTemplate.rules.editTitle', 'Edit Rule')
          : t('jobTemplate.tasks.editTitle', 'Edit Task')
      }
    />
  ) : null;

  return (
    <div className='flex h-full items-stretch justify-end overflow-y-auto'>
      <SideLayout aria-busy={isBusy}>
        <SideLayout.Head
          onClose={() => {
            if (onClose) {
              onClose();
            } else {
              navigate('..');
            }
          }}
        >
          {template
            ? clone
              ? `Clone ${template.name}`
              : `Edit ${template.name}`
            : t('newTemplate')}
        </SideLayout.Head>
        <SideLayout.Body>
          <Prompt when={!isBusy && (isDirty || isTasksDirty)} />
          <Controller
            name='status'
            control={control}
            render={({ field }) => (
              <ControlledStatusToggle
                checked={field.value === 'Active'}
                onChange={(event) =>
                  field.onChange(event.target.checked ? 'Active' : 'Inactive')
                }
              />
            )}
          />

          {template && !clone && (
            <>
              <Controller
                name='behavior'
                control={control}
                render={({ field }) => (
                  <TemplateTypeDisplay value={field.value} />
                )}
              />
              {behavior === 'asset_rules' && (
                <Accordion
                  defaultOpen={-1}
                  onToggledPanel={handleQuickCreatePanelToggle}
                  openPanel={quickCreatePanelIdx}
                >
                  <Accordion.GreyCard>
                    <Accordion.Button
                      summary={
                        watch('quickCreate')
                          ? t('jobTemplate.quickCreate.enabled', 'Enabled')
                          : t('jobTemplate.quickCreate.disabled', 'Disabled')
                      }
                    >
                      {t('jobTemplate.quickCreate.label', 'Quick Create')}
                    </Accordion.Button>
                    <Accordion.Panel>
                      <Controller
                        name='quickCreate'
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={field.value}
                            onChange={field.onChange}
                            label={t(
                              'jobTemplate.quickCreate.label',
                              'Quick Create'
                            )}
                            help={t(
                              'jobTemplate.quickCreate.help',
                              'Enable quick create for this asset-driven template'
                            )}
                            type='field'
                          />
                        )}
                      />
                    </Accordion.Panel>
                  </Accordion.GreyCard>
                </Accordion>
              )}
            </>
          )}

          <Accordion
            defaultOpen={openPanelIdx}
            onToggledPanel={handlePanelToggle}
            openPanel={openPanelIdx}
          >
            {!(template && !clone) && (
              <Accordion.GreyCard>
                <Accordion.Button
                  summary={setupInfo}
                  help='Choose template type'
                >
                  Setup
                </Accordion.Button>
                <Accordion.Panel>
                  <Controller
                    name='behavior'
                    control={control}
                    render={({ field }) => (
                      <TemplateTypeSelector
                        value={field.value}
                        onChange={field.onChange}
                        disabled={!!template && !clone}
                      />
                    )}
                  />
                  {behavior === 'asset_rules' && (
                    <div className='mt-4'>
                      <Controller
                        name='quickCreate'
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={field.value}
                            onChange={field.onChange}
                            label={t(
                              'jobTemplate.quickCreate.label',
                              'Quick Create'
                            )}
                            help={t(
                              'jobTemplate.quickCreate.help',
                              'Enable quick create for this asset-driven template'
                            )}
                            type='field'
                          />
                        )}
                      />
                    </div>
                  )}
                </Accordion.Panel>
              </Accordion.GreyCard>
            )}

            <Accordion.GreyCard error={!!errors.name}>
              <Accordion.Button
                summary={whatInfo}
                help={errors.name ? 'Template name is required' : 'Description'}
                error={!!errors.name}
              >
                What
              </Accordion.Button>
              <Accordion.Panel>
                {/* 
                  TODO: If you add/update fields in JobWhatSection, also update AutomationForm.templateData 
                  to keep them consistent. Fields that must match:
                  - name → AutomationForm.templateData.name
                  - tasks → AutomationForm.templateData.tasks
                  - notes → AutomationForm.templateData.notes
                  - tags → AutomationForm.templateData.tags
                  - includeAttributes → AutomationForm.templateData.includeAttributes
                  - jobAttributes → AutomationForm.templateData.jobAttributes
                  - attachments → AutomationForm.templateData.attachments
                */}
                <JobWhatSection
                  control={control}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  errors={errors}
                  context='template'
                  isMobile={isMobile}
                  jobTemplates={templateOptions}
                  allTags={allTags}
                  guestyEnabled={guestyEnabled}
                  taskState={taskState}
                  onShowTask={setEditTask}
                  onTaskTypeUpdate={updateTaskType}
                  showJobName={true}
                  showEnforceOrder={false}
                  showTemplateSelector={false}
                  onUseTemplateSelect={handleUseTemplateSelect}
                  templateBehavior={behavior}
                  isTaskTypeEditable={isTaskTypeEditable}
                />
              </Accordion.Panel>
            </Accordion.GreyCard>
          </Accordion>
        </SideLayout.Body>
        <SideLayout.Foot className='rounded p-4'>
          <Button title='saveTemplate' onClick={submit} loading={isBusy}>
            {isBusy
              ? 'Submitting...'
              : !templateId || clone
              ? t('saveTemplate')
              : t('updateTemplate')}
          </Button>
        </SideLayout.Foot>
      </SideLayout>
      {taskSection}
    </div>
  );
};

/**
 * Converts form values to JobTemplateInput format
 *
 * TODO: If you add/update fields in this function, also update AutomationForm.templateData
 * initialization and toInput conversion to keep them consistent.
 */
export function toInput(
  tasks: TaskInput[],
  values: Partial<FormValues>
): JobTemplateInput {
  invariant(values.name, 'Name is required');

  // Ensure behavior is properly typed as TemplateBehavior enum
  const behavior = toGraphQLBehavior(values.behavior ?? 'static');

  // Ensure notes is string | null
  const notes = serializeNotes(values.notes);

  // Ensure quickCreate is boolean
  const quickCreate = values.quickCreate ?? false;

  return {
    tasks: tasks.map((task) => ({
      id: task.id,
      type: task.type,
      name: task.name,
      description: task.description,
      attributes: task.attributes, // include attributes (site attributes to display)
      jobAttributes: task.jobAttributes, // job attributes to collect
      attribute: task.attribute, // attribute task
      purchase: task.purchase,
      stocktake: task.stocktake,
      asset: task.asset,
      tagIds: task.tagIds,
      _destroy: task._destroy, // include _destroy flag for deletions
    })),
    name: values.name,
    behavior: behavior as TemplateBehavior, // toGraphQLBehavior returns 'Static' | 'AssetRules' which matches TemplateBehavior enum
    tags: values.tags ?? null,
    notes: notes,
    status: values.status ?? Status.Active,
    includeAttributes: values.includeAttributes ?? null,
    jobAttributes: values.jobAttributes ?? null,
    attachments: (values.attachments ?? []).map(toAttachmentInput),
    guestyCleaningStatus: values.guestyCleaningStatus ? 'clean' : null,
    quickCreate: quickCreate,
  };
}

export function serialize(input: JobTemplateInput) {
  const formData = new FormData();
  formData.append('input', JSON.stringify(input));
  return formData;
}

export function unserialize(formData: globalThis.FormData): JobTemplateInput {
  const input = parseJson(formData.get('input')?.toString() ?? '');
  if (!input) {
    throw new Error('Invalid input');
  }
  return input;
}
